import React, { useState, useEffect } from "react";

import {
  Box,
  TextField,
  Typography,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  MenuItem,
  Button,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import theme from "../../theme";
import DescriptionIcon from "@mui/icons-material/Description";
import {
  fetchSuppliersToShow,
  fetchExistingSuppliers,
  fetchTaxonomy,
} from "../../lib/api_routes";
import { CountryDropDown } from "../ReusableComponents/CountriesDropDown";
import { useTaxonomy } from "../../contexts/TaxonomyContext";
import { createTaxonomyPath } from "../../utils/functions";
import { GenerateSuppliersData } from "../GenerateSuppliersData/GenerateSuppliersData";
import { OpenFormButton } from "../ReusableComponents/OpenFormButton";
import { useAccountInfo } from "../../hooks/useAccountInfo";
import { useActivityTracking } from "../../hooks/userActivityTracking";
import { formatAxiosError } from "../../utils/error_message";

function DropdownItem(props) {
  const {
    label,
    placeholder,
    value,
    disabled,
    error,
    onChange,
    options = {},
    optionKey,
    required,
  } = props;
  const labelId = `${label}-select-label`;
  const selectId = `${label}-select`;
  const placeholderToUse = placeholder ?? `Please select a ${label}`;

  return (
    <FormControl
      fullWidth
      required={required}
      error={!!error}
      size="small"
      sx={{ mb: 0.5 }}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        id={selectId}
        label={label}
        placeholder={placeholderToUse}
        value={value}
        disabled={disabled}
        onChange={onChange}
      >
        {options[optionKey]?.map((category, index) => (
          <MenuItem key={`${category}-${index}`} value={category}>
            {category}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
}

export function GenerateSuppliersForm() {
  const DEFAULT_FORM_VALUE = {
    sector: "",
    category: "",
    L2: "",
    L3: "",
    requirements: "",
    country: "",
    context: "",
    region: "",
    equipment: "",
  };
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [suppliersToShow, setSuppliersToShow] = useState([]);
  const [existingSuppliers, setExistingSuppliers] = useState([]);
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [errors, setErrors] = useState({});
  const { setTaxonomyPath } = useTaxonomy();
  const [isFormOpen, setIsFormOpen] = useState(true);
  const [loadedCount, setLoadedCount] = useState(0);
  const [requirementsCount, setRequirementsCount] = useState(0);
  const totalToLoad = 2; // Because we have 2 API calls
  const MAX_REQUIREMENTS_LENGTH = 1000;
  const MAX_CONTEXT_LENGTH = 255;
  // Account Info from hooks
  const { accountInfo } = useAccountInfo();
  // Form values
  const [values, setValues] = useState(DEFAULT_FORM_VALUE);
  // USER ACTIVITY TRACKING
  const { trackAction, resetSession } = useActivityTracking(
    "supplier_finder",
    accountInfo?.accountId,
  );

  const handleChange = (field, value) => {
    setValues((prevValues) => ({
      ...prevValues,
      [field]: value,
    }));

    // Track characters count for requirements field
    if (field === "requirements") {
      setRequirementsCount(value.length);
    }
    // Clear the error for this field if it exists
    if (errors[field]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: undefined,
      }));
    }
  };

  // Get category L1 options on load
  useEffect(() => {
    if (!accountInfo?.accountId) return;
    fetchTaxonomy("l1", null, accountInfo?.accountId).then((data) =>
      setDropdownOptions({ category: data }),
    );
  }, [accountInfo?.accountId]);

  // Fetch L2 categories based on selected L1 category
  useEffect(() => {
    if (values.category !== "") {
      fetchTaxonomy("l2", values, accountInfo?.accountId)
        .then((data) => {
          setDropdownOptions({
            ...dropdownOptions,
            L2: data,
            L3: undefined,
            equipment: ["Products", "Equipment", "Services"],
          });
        })
        .catch((error) => {
          console.error("Error fetching L2 categories:", error);
          setDropdownOptions({
            ...dropdownOptions,
            L2: undefined,
            L3: undefined,
            equipment: undefined,
          });
        });
    } else {
      setDropdownOptions({
        ...dropdownOptions,
        L2: undefined,
        L3: undefined,
        equipment: undefined,
      });
    }
  }, [values.category]);

  // Fetch L3 categories based on selected L2 category
  useEffect(() => {
    if (values.L2 !== "") {
      fetchTaxonomy("l3", values, accountInfo?.accountId)
        .then((data) => {
          setDropdownOptions({ ...dropdownOptions, L3: data });
        })
        .catch((error) => {
          console.error("Error fetching L2 categories:", error);
          setDropdownOptions({ ...dropdownOptions, L3: undefined });
        });
    } else {
      setDropdownOptions({ ...dropdownOptions, L3: undefined });
    }
  }, [values.L2]);

  // Fetch equipment / service / product options
  useEffect(() => {
    const hasRequiredFields = values.category;
    if (hasRequiredFields) {
      const equipmentOptions = ["Products", "Equipment", "Services"];
      setDropdownOptions((prevOptions) => {
        const newOptions = {
          ...prevOptions,
          equipment: equipmentOptions,
        };
        return newOptions;
      });
    } else {
      setDropdownOptions((prevOptions) => {
        const newOptions = {
          ...prevOptions,
          equipment: undefined,
        };
        return newOptions;
      });
    }
  }, [values.category]);

  // Add open form button logic
  const openForm = () => {
    setIsLoaded(false);
    setSuppliersToShow([]);
    setExistingSuppliers([]);
    setIsFormOpen(true);
    setTaxonomyPath("");
    resetSession();
  };

  // Show taxonomy values on the page
  useEffect(() => {
    if (accountInfo) {
      const path = createTaxonomyPath(values, accountInfo); // compute path
      setTaxonomyPath(path); // update context
    }
  }, [values, accountInfo, setTaxonomyPath]);

  // Logic for CircularProgress
  const incrementLoadedCount = () => setLoadedCount((prev) => prev + 1);

  const formItems = [
    {
      type: "Dropdown",
      label: "Category",
      placeholder: "Please select a category",
      value: values.category || "",
      disabled: accountInfo?.category != null,
      key: "category",
      error: errors.category,
      required: true,
    },
    {
      type: "Dropdown",
      label: "Category L2",
      placeholder: "Please select a category L2",
      value: values.L2 || "",
      disabled: values.category === "",
      key: "L2",
      error: errors.L2,
      required: true,
    },
    {
      type: "Dropdown",
      label: "Category L3",
      placeholder: "Please select a category L3",
      value: values.L3 || "",
      disabled: values.L2 === "",
      key: "L3",
      error: errors.L3,
    },
    {
      type: "TextField",
      placeholder: "Please enter the requirements",
      key: "requirements",
      multiline: true,
      minRows: 3,
      slotProps: { htmlInput: { maxLength: MAX_REQUIREMENTS_LENGTH } },
      showCounter: true,
    },
    {
      type: "CountryDropdown",
      key: "country",
      value: values.country || "",
      required: true,
      error: errors.country,
    },
    {
      type: "TextField",
      placeholder: "Context (optional)",
      key: "context",
      slotProps: { htmlInput: { maxLength: MAX_CONTEXT_LENGTH } },
      showCounter: true,
    },
    {
      type: "TextField",
      placeholder: "Region",
      key: "region",
    },
    {
      type: "Dropdown",
      label: "Products / Equipment / Service",
      placeholder: "Please select an equipment or service",
      disabled: !values.category,
      value: values.equipment || "",
      key: "equipment",
      error: errors.equipment,
    },
  ];

  const getFormItem = (data, key) => {
    if (data.type === "Dropdown") {
      return (
        <DropdownItem
          key={key}
          optionKey={data.key}
          required={data.required}
          label={data.label}
          placeholder={data.placeholder}
          value={data.value}
          disabled={data.disabled}
          error={data.error}
          onChange={(e) => handleChange(data.key, e.target.value)}
          options={dropdownOptions}
        />
      );
    } else if (data.type === "TextField") {
      const isRequirements = data.key === "requirements";
      const isLimit =
        isRequirements && requirementsCount >= MAX_REQUIREMENTS_LENGTH;
      const isContext = data.key === "context";
      const contextCount = (values.context || "").length;
      const isContextOverLimit = isContext && contextCount > MAX_CONTEXT_LENGTH;
      return (
        <>
          <TextField
            key={key}
            required={data.required}
            error={!!errors[data.key] || isContextOverLimit}
            placeholder={data.placeholder}
            onChange={(e) => handleChange(data.key, e.target.value)}
            multiline={data.multiline}
            minRows={data.minRows}
            slotProps={data.slotProps}
            value={values[data.key] || ""}
            sx={{ mb: isRequirements || isContext ? 0 : 0.5 }}
          />
          {isRequirements && (
            <Box
              sx={{
                mb: 0.5,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: isLimit ? "error.main" : "text-secondary",
                  fontSize: "0.75rem",
                  pr: 1.5,
                }}
              >
                {isLimit &&
                  `You've reached the maximum of ${MAX_REQUIREMENTS_LENGTH} characters - `}
                {requirementsCount}/{MAX_REQUIREMENTS_LENGTH}
              </Typography>
            </Box>
          )}
          {isContext && (
            <Box
              sx={{
                mb: 0.5,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: isContextOverLimit ? "error.main" : "text-secondary",
                  fontSize: "0.75rem",
                  pr: 1.5,
                }}
              >
                {isContextOverLimit &&
                  `Context must not exceed ${MAX_CONTEXT_LENGTH} characters - `}
                {contextCount}/{MAX_CONTEXT_LENGTH}
              </Typography>
            </Box>
          )}
        </>
      );
    } else if (data.type === "CountryDropdown") {
      return (
        <FormControl
          fullWidth
          key={key}
          required={data.required}
          error={!!data.error}
          size="small"
          sx={{ mb: 0.5 }}
        >
          <CountryDropDown
            required={data.required}
            error={data.error}
            value={data.value || ""}
            onChange={(country) => handleChange("country", country)}
          />
          {data.error && <FormHelperText>{data.error}</FormHelperText>}
        </FormControl>
      );
    } else {
      return <React.Fragment key={key} />;
    }
  };

  const validateForm = () => {
    const ERROR_MESSAGES = {
      category: "Category must be filled",
      L2: "Category L2 must be filled",
      L3: "Category L3 must be filled",
      L4: "Category L4 must be filled",
      country: "Country must be filled",
    };
    const newErrors = {};
    const requiredFields = ["country", "L2", "category"];

    Object.entries(values).forEach(([key, value]) => {
      if (requiredFields.includes(key) && (value === "" || !value)) {
        console.log(key, value);
        const msg = ERROR_MESSAGES[key];
        newErrors[key] = msg;
      }
    });

    // Context is optional, but must not exceed the maximum length when populated
    if (values.context && values.context.length > MAX_CONTEXT_LENGTH) {
      newErrors.context = `Context must not exceed ${MAX_CONTEXT_LENGTH} characters`;
    }

    setErrors(newErrors);
    return newErrors;
  };

  const clearForm = () => {
    setValues({ ...DEFAULT_FORM_VALUE, sector: values.sector });
    setErrors({});
    setIsLoaded(false);
    setRequirementsCount(0);
    setSuppliersToShow([]);
    setExistingSuppliers([]);
  };

  const submitForm = async () => {
    const newErrors = validateForm();
    const hasErrors = Object.keys(newErrors).length > 0;

    if (!hasErrors) {
      setIsLoading(true);
      setLoadedCount(0);
      try {
        // Fetch both datasets
        const apiCalls = [
          fetchSuppliersToShow(values),
          fetchExistingSuppliers(values),
        ];
        // API calls with progress tracking
        const results = await Promise.all(
          apiCalls.map((call) =>
            call.then((res) => {
              incrementLoadedCount();
              return res;
            }),
          ),
        );

        const [suppliersData, existingSuppliersData] = results;

        // USER ACTIVITY TRACKING - Track success
        await trackAction("Generate Supplier Finder", "SUCCESS", null, values);

        // Clean and format data
        const cleanedSupplierData = suppliersData.map((row) => ({
          "Supplier Name": row["Supplier"] || "",
          "National Status": row["National_Status"] || "",
          "Service Type": row["Service_Type"] || "",
          Website: row["Website"] || "",
          Accreditations: row["Accreditations"] || "",
          Turnover: row["Turnover"] || "",
          Description: row["Description"] || "",
        }));

        const formattedExistingSuppliers = existingSuppliersData.map((row) => ({
          "Supplier Name": row["Normalised_Supplier_Names"] || "",
          Description: row.Description || "",
          "Category L1": row.L1 || "",
          "Category L2": row.L2 || "",
          "Category L3": row.L3 || "",
          "Category L4": row.L4 || "",
        }));

        setSuppliersToShow(cleanedSupplierData);
        setExistingSuppliers(formattedExistingSuppliers);
        setIsLoaded(true);
        setIsFormOpen(false);
      } catch (error) {
        const { serverMessage, fullError } = await formatAxiosError(error);
        alert(serverMessage ?? "Failed generating suppliers.");
        // USER ACTIVITY TRACKING - Track failure and reset so retry starts fresh
        await trackAction(
          "Generate Supplier Finder",
          "FAILED",
          fullError,
          values,
        ).catch(console.error);
        resetSession();
      } finally {
        setIsLoading(false);
      }
    } else {
      console.log("Form has errors:", newErrors);
    }
  };

  return (
    <>
      {isFormOpen &&
        !isLoaded &&
        !isLoading &&
        suppliersToShow.length === 0 && (
          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: { xs: "100%", sm: "400px" },
              gap: 1.5,
              maxWidth: 500,
            }}
          >
            <Typography
              variant="h5"
              sx={{ mb: 2, textAlign: "center", fontWeight: 500 }}
            >
              Generate Suppliers
            </Typography>
            <TextField
              required
              id="sector-input"
              label="Sector"
              placeholder="Please enter a sector"
              fullWidth
              size="small"
              value={values.sector || accountInfo?.sector || ""}
              onChange={(e) => handleChange("sector", e.target.value)}
              error={!!errors.sector}
              helperText={errors.sector}
              disabled={!accountInfo?.editableSector}
              sx={{ mb: 0.5 }}
            />
            {formItems.map((item) => getFormItem(item, item.key))}
            <Typography
              variant="body2"
              sx={{ textAlign: "center", mt: 1.5, color: "text.secondary" }}
            >
              Press below to generate the suppliers...
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "center",
                mt: 1.5,
                mb: 2,
                gap: { xs: 1, sm: 2 },
              }}
            >
              <Button
                variant="contained"
                onClick={submitForm}
                disabled={isLoading}
                sx={{
                  width: { xs: "100%", sm: "175px" },
                  whiteSpace: "nowrap",
                  minWidth: { sm: "160px" },
                  py: 0.75,
                }}
              >
                Generate suppliers
              </Button>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  width: { xs: "100%", sm: "175px" },
                  minWidth: { sm: "160px" },
                  py: 0.75,
                }}
                onClick={clearForm}
              >
                Clear form
              </Button>
            </Box>
          </Box>
        )}
      {isLoading && (
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={60} />
          <Typography sx={{ mt: 2 }}>
            Loaded {loadedCount} of {totalToLoad} data sources...
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(loadedCount / totalToLoad) * 100}
            sx={{
              mt: 2,
              height: 10,
              borderRadius: 5,
            }}
          />
        </Box>
      )}

      {isLoaded && suppliersToShow && suppliersToShow.length > 0 && (
        <Box
          sx={{
            position: "fixed",
            top: "5.7rem",
            right: "2.5rem",
            zIndex: 1300,
          }}
        >
          <OpenFormButton
            onClick={openForm}
            tooltip="Open Form"
            icon={DescriptionIcon}
            variant="outlined"
          />
        </Box>
      )}

      {isLoaded && suppliersToShow && suppliersToShow.length > 0 && (
        <GenerateSuppliersData
          suppliersToShow={suppliersToShow}
          existingSuppliers={existingSuppliers}
        />
      )}

      {isLoaded && (!suppliersToShow || suppliersToShow.length === 0) && (
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No suppliers found with the current criteria.
          </Typography>
        </Box>
      )}
    </>
  );
}
